import { useMemo, useState } from "react";
import { Button, Checkbox, Icon, Input, Table } from "semantic-ui-react";
import {
  cellKey, cellValue, changesByDate, datesBetween, dayCostPaise, gridRows, hasEstimate, isQuantity,
  paiseToRupees, rowCostPaise, rowTotal, stepQuantity,
} from "./billGrid";
import { formatRupees, isMoney } from "../../../utils/money";

const dayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

/* Items down the side, days of the stay across — the same shape as the
   guest houses' "Fooding date wise" sheet. Edits are held here until
   "Save meals", which sends one request per changed day. The last row takes
   boarding as one amount instead, for a guest house that has already worked
   the food bill out itself. */
export default function BillMealsGrid({ bill, items, editable, busy, onSave, onSaveLumpSum }) {
  const [drafts, setDrafts] = useState({});
  const dates = useMemo(() => datesBetween(bill.stayDates?.from, bill.stayDates?.to), [bill.stayDates]);
  const rows = useMemo(() => gridRows(bill.lines, items), [bill.lines, items]);
  const changes = useMemo(() => changesByDate(rows, drafts), [rows, drafts]);
  const invalid = Object.values(drafts).some((v) => !isQuantity(v));
  const changedDays = Object.keys(changes).length;

  const lumpSum = bill.lines.find((l) => l.kind === "CUSTOM" && l.detail.lump_sum) || null;
  const lumpSumRow = (
    <LumpSumEntry key={lumpSum?.id ?? "none"} line={lumpSum} editable={editable} busy={busy} onSave={onSaveLumpSum} />
  );

  if (!dates.length) return <p style={{ opacity: 0.7 }}>The stay has no dates yet.</p>;
  if (!rows.length) {
    return (
      <div>
        <p style={{ opacity: 0.7 }}>No items are set up for the daily grid. Add them under Manage → Billing, or enter boarding as one amount:</p>
        {lumpSumRow}
      </div>
    );
  }
  const estimated = hasEstimate(rows, drafts);
  const grandPaise = rows.reduce((acc, r) => acc + rowCostPaise(r, dates, drafts), 0);

  const save = async () => {
    if (await onSave(changes)) setDrafts({});
  };

  return (
    <div>
      <style>{`
        .bhu-meals { overflow-x: auto; }
        .bhu-meals table.ui.table { width: auto; }
        .bhu-meals th, .bhu-meals td { white-space: nowrap; }
        .bhu-meals td.bhu-cell { padding: 3px 4px !important; }
        .bhu-meals .bhu-sticky { position: sticky; left: 0; z-index: 1; background: #f9fafb; }
        .bhu-stepper { display: inline-flex; align-items: center; gap: 2px; }
        .bhu-stepper .ui.button { margin: 0; padding: 5px; }
        .bhu-stepper input { width: 40px !important; padding: 4px 2px !important; text-align: center; }
        .bhu-meals tfoot th { background: #f3f4f5 !important; }
        .bhu-meals .bhu-est { color: #b35c00; }
      `}</style>
      <div className="bhu-meals">
        <Table compact celled unstackable size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="bhu-sticky">Item</Table.HeaderCell>
              {dates.map((d) => <Table.HeaderCell key={d} textAlign="center">{dayLabel(d)}</Table.HeaderCell>)}
              <Table.HeaderCell textAlign="center">Qty</Table.HeaderCell>
              <Table.HeaderCell textAlign="right" title="Price × quantity, before GST">Cost</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => (
              <Table.Row key={row.itemId}>
                <Table.Cell className="bhu-sticky">
                  <strong>{row.name}</strong>
                  {row.unitPrice != null && (
                    <span style={{ opacity: 0.6, fontSize: "0.85em" }}> · {formatRupees(row.unitPrice)}</span>
                  )}
                  {!row.isActive && <span style={{ opacity: 0.6, fontSize: "0.85em" }}> (switched off)</span>}
                </Table.Cell>
                {dates.map((d) => {
                  const key = cellKey(row.itemId, d);
                  const value = cellValue(row, d, drafts);
                  const changed = key in drafts;
                  const set = (v) => setDrafts((prev) => ({ ...prev, [key]: v }));
                  const locked = busy || (!row.isActive && !row.cells[d]);
                  return (
                    <Table.Cell key={d} textAlign="center" className="bhu-cell"
                      style={{ background: changed ? "#fff8e1" : undefined }}>
                      {editable ? (
                        <QuantityStepper
                          label={`${row.name} on ${dayLabel(d)}`} value={value} disabled={locked}
                          onChange={set}
                        />
                      ) : (
                        value || ""
                      )}
                    </Table.Cell>
                  );
                })}
                <Table.Cell textAlign="center"><strong>{rowTotal(row, dates, drafts) || ""}</strong></Table.Cell>
                <Table.Cell textAlign="right">
                  {rowCostPaise(row, dates, drafts) ? formatRupees(paiseToRupees(rowCostPaise(row, dates, drafts))) : ""}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell className="bhu-sticky"><strong>Total</strong></Table.HeaderCell>
              {dates.map((d) => {
                const paise = dayCostPaise(rows, d, drafts);
                return (
                  <Table.HeaderCell key={d} textAlign="center">
                    {paise ? formatRupees(paiseToRupees(paise)) : ""}
                  </Table.HeaderCell>
                );
              })}
              <Table.HeaderCell />
              <Table.HeaderCell textAlign="right" className={estimated ? "bhu-est" : undefined}>
                <strong>{formatRupees(paiseToRupees(grandPaise))}</strong>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
      {editable && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
          <Button primary size="small" disabled={busy || invalid || !changedDays} loading={busy && changedDays > 0}
            onClick={save}>
            <Icon name="save" />Save meals
          </Button>
          {Object.keys(drafts).length > 0 && (
            <Button basic size="small" disabled={busy} onClick={() => setDrafts({})}>Undo changes</Button>
          )}
          {invalid && <span style={{ color: "#9f3a38" }}>Counts must be numbers like 2 or 0.5.</span>}
          {!invalid && changedDays > 0 && (
            <span style={{ color: "#b35c00" }}>Unsaved changes on {changedDays} day(s) — costs in orange are estimates until saved.</span>
          )}
        </div>
      )}
      <div style={{ fontSize: "0.85em", opacity: 0.7, margin: "4px 0 10px" }}>
        Costs are before GST; GST is added on the bill where it applies.
      </div>
      {lumpSumRow}
    </div>
  );
}

/* Boarding as one figure: e.g. the guest house already worked the food bill
   out at ₹1,200. It goes on the bill under the meals' heading (Boarding) as
   one line. Clearing the amount and saving removes it. */
function LumpSumEntry({ line, editable, busy, onSave }) {
  const [amount, setAmount] = useState(line ? String(Number(line.unitPrice)) : "");
  const [gst, setGst] = useState(line ? line.gstApplicable : false);
  const valid = amount.trim() === "" || isMoney(amount);
  const unchanged = (line ? String(Number(line.unitPrice)) : "") === amount.trim()
    && (line ? line.gstApplicable : false) === gst;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      padding: "8px 10px", background: "#f9fafb", border: "1px solid rgba(34,36,38,.15)", borderRadius: 4 }}>
      <strong>Boarding as one amount</strong>
      {editable ? (
        <>
          <Input size="small" label={{ basic: true, content: "₹" }} placeholder="e.g. 1200" style={{ width: 150 }}
            inputMode="decimal" value={amount} error={!valid} disabled={busy}
            input={{ "aria-label": "Boarding amount" }} onChange={(_, d) => setAmount(d.value)} />
          <Checkbox id="lump-gst" label="Charge GST" checked={gst} disabled={busy} onChange={(_, d) => setGst(d.checked)} />
          <Button size="small" primary disabled={busy || !valid || unchanged}
            onClick={() => onSave({ amount: amount.trim(), gst_applicable: gst })}>
            {line && amount.trim() === "" ? "Remove" : "Save"}
          </Button>
          <span style={{ fontSize: "0.85em", opacity: 0.7 }}>
            Before GST. Shown on the bill as {line?.categoryName || "Boarding"}.
          </span>
        </>
      ) : (
        <span>{line ? `${formatRupees(line.unitPrice)} (${line.categoryName})` : "—"}</span>
      )}
      {line && (
        <span style={{ marginLeft: "auto" }}>On the bill: <strong>{formatRupees(line.total)}</strong></span>
      )}
    </div>
  );
}

/* − [ n ] +  — the minus and plus step one unit; the box takes any count
   typed in (0.5 for a half portion). */
function QuantityStepper({ label, value, disabled, onChange }) {
  return (
    <span className="bhu-stepper">
      <Button type="button" icon basic size="mini" disabled={disabled || !value}
        aria-label={`One less ${label}`} onClick={() => onChange(stepQuantity(value, -1))}>
        <Icon name="minus" />
      </Button>
      <Input size="mini" inputMode="decimal" value={value} error={!isQuantity(value)} disabled={disabled}
        input={{ "aria-label": label }} onChange={(_, data) => onChange(data.value)} />
      <Button type="button" icon basic size="mini" disabled={disabled}
        aria-label={`One more ${label}`} onClick={() => onChange(stepQuantity(value, 1))}>
        <Icon name="plus" />
      </Button>
    </span>
  );
}
